import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import axiosInstance from "../../config/axiosApi/axiosApi";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import SimpleDateRangePicker from "../../components/CustomComponents/SimpleDateRangePicker/SimpleDateRangePicker";
import { setVisibleColumns, setSortOrder, setSortBy, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const RESERVATION_SORT_FIELDS = {
  customerName: "CustomerName",
  dateTime: "ReservationDate",
};

const RESERVATION_SORT_LABELS = {
  [RESERVATION_SORT_FIELDS.customerName]: "Customer",
  [RESERVATION_SORT_FIELDS.dateTime]: "Date & Time",
};

const ManageReservations = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [allReservations, setAllReservations] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const reservationsFetchSeqRef = useRef(0);

  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();

  

  const pageKey = "reservations";
  const defaultVisibleColumns = {
    customerName: true,
    dateTime: true,
    guests: true,
    reservationType: false,
    tableNo: true,
    textReminder: true,
    status: true,
  };
  
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "ReservationDate", label: "Date & Time" };
  const defaultReservationTypeFilter = null;
  const defaultReservationStatusFilter = null;
  const defaultFilterStartDate = "";
  const defaultFilterEndDate = "";
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const reservationStatusFilter = preferences.reservationStatusFilter ?? defaultReservationStatusFilter;
  const filterStartDate = preferences.filterStartDate || defaultFilterStartDate;
  const filterEndDate = preferences.filterEndDate || defaultFilterEndDate;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: RESERVATION_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const reservationStatusOptions = [
    { value: null, label: "All Statuses" },
    { value: "Pending", label: "Pending" },
    { value: "Upcoming", label: "Upcoming" },
    { value: "Notified", label: "Notified" },
    { value: "Wrong Number", label: "Wrong Number" },
    { value: "No Answer", label: "No Answer" },
    { value: "Called", label: "Called" },
    { value: "Running late", label: "Running late" },
    { value: "Waiting", label: "Waiting" },
    { value: "Seated", label: "Seated" },
    { value: "Partially Arrived", label: "Partially Arrived" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Rejected", label: "Rejected" },
    { value: "No Show", label: "No Show" },
    { value: "Completed", label: "Completed" },
  ];

  const reservationStatusSelectValue =
    reservationStatusOptions.find((opt) => opt.value === reservationStatusFilter) ||
    reservationStatusOptions[0];

  const hasFiltersApplied = () =>
    search.trim() !== "" ||
    reservationStatusFilter != null ||
    !!filterStartDate ||
    !!filterEndDate;

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "reservationStatusFilter", value: defaultReservationStatusFilter }));
    dispatch(setFilterField({ pageKey, field: "filterStartDate", value: defaultFilterStartDate }));
    dispatch(setFilterField({ pageKey, field: "filterEndDate", value: defaultFilterEndDate }));
  };

  const getReservationTypeBadge = (reservation) => {
    const type = reservation.reservationType || "Regular";
    const isPrivate = reservation.isPrivateReservation || false;
    const isEvent = reservation.isEventReservation || false;
    
    // Determine actual type
    let actualType = type;
    if (isEvent) actualType = "Event";
    else if (isPrivate) actualType = "Private";
    else if (type === "Regular") actualType = "Regular";
    
    const badgeColors = {
      Regular: { bg: "#17a2b8", color: "#ffffff" },
      Private: { bg: "#6f42c1", color: "#ffffff" },
      Event: { bg: "#fd7e14", color: "#ffffff" },
    };
    
    const colors = badgeColors[actualType] || badgeColors.Regular;
    
    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: colors.bg,
          color: colors.color,
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        {actualType === "Private" && <i className="fa fa-lock" style={{ fontSize: "10px" }}></i>}
        {actualType === "Event" && <i className="fa fa-calendar-star" style={{ fontSize: "10px" }}></i>}
        {actualType === "Regular" && <i className="fa fa-table" style={{ fontSize: "10px" }}></i>}
        {actualType}
      </span>
    );
  };

  const formatTablesWithServiceArea = (reservation) => {
    // If it's a private or event reservation, show service area
    if (reservation.isPrivateReservation || reservation.isEventReservation) {
      return "Service Area";
    }

    // If we have reservationTables with service area info
    if (reservation.reservationTables && Array.isArray(reservation.reservationTables) && reservation.reservationTables.length > 0) {
      // Group tables by service area
      const groupedByServiceArea = {};
      reservation.reservationTables.forEach((table) => {
        // Service area name now comes directly from the API via JOIN
        const serviceAreaName = table.serviceAreaName || (table.serviceAreaID ? `Area ${table.serviceAreaID}` : "Unknown Area");
        if (!groupedByServiceArea[serviceAreaName]) {
          groupedByServiceArea[serviceAreaName] = [];
        }
        groupedByServiceArea[serviceAreaName].push(table.tableName || `Table ${table.tableID}`);
      });

      // Format: "Table1, Table2 (Service Area Name)"
      return Object.entries(groupedByServiceArea)
        .map(([areaName, tableNames]) => {
          if (tableNames.length > 0) {
            return `${tableNames.join(", ")} (${areaName})`;
          }
          return areaName;
        })
        .join(" | ");
    }

    // Fallback to tableNames if available
    if (reservation.tableNames && reservation.tableNames.trim()) {
      return reservation.tableNames;
    }

    return "N/A";
  };

  const totalRecords = allReservations.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const reservations = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return allReservations.slice(start, start + size);
  }, [allReservations, pageNumber, pageSize]);

  const fetchReservationsData = useCallback(async () => {
    const fetchSeq = ++reservationsFetchSeqRef.current;
    const formatLocalDateForApi = (value) => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      setLoading(true);
      const params = {
        CompID,
        Search: search?.trim() || "",
        SortBy: sortBy?.value || defaultSortBy.value,
        SortOrder: sortOrder?.value || defaultSortOrder.value,
        ReservationType: "",
        ReservationStatus: reservationStatusFilter || "",
      };

      // Avoid sending empty/null date query params because [ApiController]
      // can reject invalid nullable DateTime binding before entering action.
      if (filterStartDate) {
        const startDate = formatLocalDateForApi(filterStartDate);
        if (startDate) {
          params.StartDate = startDate;
        }
      }

      if (filterEndDate) {
        const endDate = formatLocalDateForApi(filterEndDate);
        if (endDate) {
          params.EndDate = endDate;
        }
      }

      const response = await axiosInstance.get(
        "/api/Reservation/BO_GetAllReservations",
        {
          params,
        }
      );
      // Response logged

      if (fetchSeq !== reservationsFetchSeqRef.current) return;

      const reservationsData = response.data.data || response.data;

      if (Array.isArray(reservationsData)) {
        setAllReservations(reservationsData);
        const noFilters =
          !search.trim() &&
          reservationStatusFilter == null &&
          !filterStartDate &&
          !filterEndDate;
        if (noFilters) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, reservationsData.length);
        }
      } else {
        setAllReservations([]);
      }
    } catch (err) {
      if (fetchSeq !== reservationsFetchSeqRef.current) return;
      showErrorToast("Failed to load reservations");
      setAllReservations([]);
    } finally {
      if (fetchSeq === reservationsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, CompID, sortBy, sortOrder, reservationStatusFilter, filterStartDate, filterEndDate]);
  // CompID logged

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // Reservation type filter is intentionally hidden; always keep it as "all".
    if (preferences.reservationTypeFilter !== defaultReservationTypeFilter) {
      dispatch(setFilterField({ pageKey, field: "reservationTypeFilter", value: defaultReservationTypeFilter }));
    }
    if (preferences.reservationStatusFilter === undefined) {
      dispatch(setFilterField({ pageKey, field: "reservationStatusFilter", value: defaultReservationStatusFilter }));
    }
    if (preferences.filterStartDate === undefined) {
      dispatch(setFilterField({ pageKey, field: "filterStartDate", value: defaultFilterStartDate }));
    }
    if (preferences.filterEndDate === undefined) {
      dispatch(setFilterField({ pageKey, field: "filterEndDate", value: defaultFilterEndDate }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
  }, [dispatch, pageKey, preferences.reservationTypeFilter]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    reservationStatusFilter,
    filterStartDate,
    filterEndDate,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(reservationsFetchSeqRef, [
    search,
    reservationStatusFilter,
    filterStartDate,
    filterEndDate,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => {
    if (CompID) fetchReservationsData();
  }, [fetchReservationsData, CompID]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    reservationStatusFilter,
    filterStartDate,
    filterEndDate,
    sortBy?.value,
    sortOrder?.value,
    pageNumber,
    pageSize?.value,
  ]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No reservations match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    if (reservationStatusFilter != null) {
      return "No reservations match the selected status. Try another status or clear filters.";
    }
    if (filterStartDate || filterEndDate) {
      return "No reservations match the selected date range. Adjust the range or clear filters.";
    }
    return "There are no reservations to show right now.";
  };

  const handleDeleteReservation = async (reservation) => {
    try {
      const customerLabel = reservation.customerName ?? reservation.reserv_CustomerName ?? "this reservation";
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete reservation for ${customerLabel}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const reservationId = reservation.id ?? reservation.reserv_ID;
      if (reservationId == null || reservationId === undefined) {
        showErrorToast("Reservation ID is missing.");
        setLoading(false);
        return;
      }
      const response = await axiosInstance.delete(
        "/api/Reservation/DeleteReservation",
        {
          params: {
            ID: reservationId,
            CompID,
          },
        }
      );

      if (response.data.status) {
        // Remove from allReservations - pagination will be handled by useEffect
        setAllReservations((prevReservations) =>
          prevReservations.filter((r) => {
            // Handle both id and reserv_ID for compatibility
            const rId = r.id || r.reserv_ID;
            return rId !== reservationId;
          })
        );
        showSuccessToast("Reservation deleted successfully");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
      setLoading(false);
    } catch (err) {
      // Error in handleDeleteReservation silently
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };



  const handleRowClick = async (reservation) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/api/Reservation/GetReservationById",
        {
          params: {
            ID: reservation.id,
            CompID,
          },
        }
      );

      const data = response.data?.data || response.data?.Data || null;
      if (!response.data?.status || !data) {
        showErrorToast(
          response.data?.message || "Failed to load reservation details"
        );
        return;
      }

      setSelectedReservation(data);
      setShowDetailModal(true);
    } catch (error) {
      showErrorToast("Failed to load reservation details");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedReservation(null);
  };

  const renderReservationDetails = () => {
    if (!selectedReservation) return null;

    const reservation = selectedReservation;
    const tables = reservation.reservationTables || [];
    const items = reservation.reservationItems || [];
    const paymentLinks = reservation.reservationPaymentLinks || [];

    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case "active":
          return "#28a745";
        case "cancelled":
          return "#dc3545";
        case "pending":
          return "#ffc107";
        default:
          return "#6c757d";
      }
    };

    const sectionCardStyle = {
      backgroundColor: isDarkMode ? "#343a40" : "#f8f9fa",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "20px",
      border: `1px solid ${isDarkMode ? "#495057" : "#dee2e6"}`,
      boxShadow: isDarkMode
        ? "0 2px 8px rgba(0, 0, 0, 0.3)"
        : "0 2px 8px rgba(0, 0, 0, 0.08)",
    };

    const sectionTitleStyle = {
      fontSize: "18px",
      fontWeight: "700",
      color: isDarkMode ? "#ffffff" : "#212529",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    };

    const infoRowStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "12px 0",
      borderBottom: `1px solid ${isDarkMode ? "#495057" : "#e9ecef"}`,
    };

    const labelStyle = {
      fontWeight: "600",
      color: isDarkMode ? "#adb5bd" : "#6c757d",
      fontSize: "14px",
      flex: "0 0 40%",
    };

    const valueStyle = {
      color: isDarkMode ? "#ffffff" : "#212529",
      fontSize: "15px",
      flex: "1",
      textAlign: "right",
    };

    return (
      <div style={{ padding: "0", maxHeight: "70vh", overflowY: "auto" }}>
        {/* Header Section */}
        <div
          style={{
            ...sectionCardStyle,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            color: "#ffffff",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "24px", fontWeight: "700" }}>
                Reservation #{reservation.id}
              </h3>
              <p style={{ margin: "8px 0 0 0", opacity: 0.9, fontSize: "14px" }}>
                {reservation.startDateTime
                  ? new Date(reservation.startDateTime).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "N/A"}
              </p>
            </div>
            <span
              style={{
                padding: "8px 16px",
                borderRadius: "20px",
                fontSize: "13px",
                fontWeight: "600",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "#ffffff",
              }}
            >
              {reservation.reservationStatus || "Pending"}
            </span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          {/* Customer Information */}
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>
              <i className="fa fa-user" style={{ color: themeColor }}></i>
              <span>Customer Information</span>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Name</div>
              <div style={valueStyle}>
                {reservation.customerName && reservation.customerName.trim()
                  ? reservation.customerName
                  : "N/A"}
              </div>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Mobile</div>
              <div style={valueStyle}>
                {reservation.customerMobileNumber || "N/A"}
              </div>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Email</div>
              <div style={valueStyle}>
                {reservation.customerEmail && reservation.customerEmail.trim()
                  ? reservation.customerEmail
                  : "N/A"}
              </div>
            </div>
          
          </div>

          {/* Reservation Details */}
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>
              <i className="fa fa-calendar-alt" style={{ color: themeColor }}></i>
              <span>Reservation Details</span>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Date & Time</div>
              <div style={valueStyle}>
                {reservation.startDateTime
                  ? new Date(reservation.startDateTime).toLocaleString()
                  : "N/A"}
              </div>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>End Time</div>
              <div style={valueStyle}>
                {reservation.endDateTime
                  ? new Date(reservation.endDateTime).toLocaleString()
                  : "N/A"}
              </div>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Number of Guests</div>
              <div style={valueStyle}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i className="fa fa-users" style={{ color: themeColor }}></i>
                  {reservation.numberofGuest || "N/A"}
                </span>
              </div>
            </div>
            {/* <div style={infoRowStyle}>
              <div style={labelStyle}>Reservation Type</div>
              <div style={valueStyle}>
                {getReservationTypeBadge(reservation)}
              </div>
            </div> */}
            <div style={infoRowStyle}>
              <div style={labelStyle}>Source</div>
              <div style={valueStyle}>
                {reservation.reservationSource || "N/A"}
              </div>
            </div>
            <div style={infoRowStyle}>
              <div style={labelStyle}>Confirmation Status</div>
              <div style={valueStyle}>
                {reservation.confirmationStatus || "N/A"}
              </div>
            </div>
            <div style={{ ...infoRowStyle, borderBottom: "none" }}>
              <div style={labelStyle}>Text Reminder</div>
              <div style={valueStyle}>
                {reservation.isCustomerWantTextReminder ? (
                  <span style={{ color: "#28a745" }}>
                    <i className="fa fa-check-circle me-1"></i>Yes
                  </span>
                ) : (
                  <span style={{ color: "#6c757d" }}>
                    <i className="fa fa-times-circle me-1"></i>No
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tables Section */}
        {(tables.length > 0 || reservation.isPrivateReservation || reservation.isEventReservation) && (
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>
              <i className="fa fa-table" style={{ color: themeColor }}></i>
              <span>
                {reservation.isPrivateReservation || reservation.isEventReservation
                  ? "Service Area Reservation"
                  : `Reserved Tables (${tables.length})`}
              </span>
            </div>
            {reservation.isPrivateReservation || reservation.isEventReservation ? (
              <div style={{
                padding: "16px",
                backgroundColor: isDarkMode ? "#495057" : "#ffffff",
                borderRadius: "8px",
                border: `1px solid ${isDarkMode ? "#5a6268" : "#dee2e6"}`,
              }}>
                <div style={{ fontWeight: "600", color: isDarkMode ? "#ffffff" : "#212529", marginBottom: "8px" }}>
                  {reservation.isEventReservation ? "Event Reservation" : "Private Reservation"}
                </div>
                <div style={{ fontSize: "14px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  This reservation blocks the entire service area. No specific table assignment required.
                </div>
              </div>
            ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "12px",
              }}
            >
               {tables.map((table, idx) => {
                 // Service area name now comes directly from the API via JOIN
                 const serviceAreaName = table.serviceAreaName || (table.serviceAreaID ? `Area ${table.serviceAreaID}` : null);
                 return (
                   <div
                     key={table.id || idx}
                     style={{
                       backgroundColor: isDarkMode ? "#495057" : "#ffffff",
                       padding: "12px",
                       borderRadius: "8px",
                       border: `1px solid ${isDarkMode ? "#5a6268" : "#dee2e6"}`,
                     }}
                   >
                     <div style={{ fontWeight: "600", color: isDarkMode ? "#ffffff" : "#212529", marginBottom: "4px" }}>
                       {table.tableName || `Table ${table.tableID}`}
                     </div>
                     {serviceAreaName && (
                       <div style={{ fontSize: "12px", color: themeColor, marginBottom: "4px", fontWeight: "500" }}>
                         <i className="fa fa-map-marker-alt me-1" style={{ fontSize: "10px" }}></i>
                         {serviceAreaName}
                       </div>
                     )}
                     <div style={{ fontSize: "12px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                       Capacity: {table.seatingCapacity} seats
                     </div>
                   </div>
                 );
               })}
            </div>
            )}
          </div>
        )}

        {/* Items Section */}
        {items.length > 0 && (
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>
              <i className="fa fa-utensils" style={{ color: themeColor }}></i>
              <span>Reservation Items ({items.length})</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: isDarkMode ? "#495057" : "#e9ecef",
                      borderBottom: `2px solid ${isDarkMode ? "#5a6268" : "#dee2e6"}`,
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: isDarkMode ? "#ffffff" : "#212529",
                        fontSize: "14px",
                      }}
                    >
                      Item Name
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: isDarkMode ? "#ffffff" : "#212529",
                        fontSize: "14px",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: isDarkMode ? "#ffffff" : "#212529",
                        fontSize: "14px",
                      }}
                    >
                      Qty
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: isDarkMode ? "#ffffff" : "#212529",
                        fontSize: "14px",
                      }}
                    >
                      Price
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: isDarkMode ? "#ffffff" : "#212529",
                        fontSize: "14px",
                      }}
                    >
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr
                      key={item.id || idx}
                      style={{
                        borderBottom: `1px solid ${isDarkMode ? "#495057" : "#e9ecef"}`,
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          color: isDarkMode ? "#ffffff" : "#212529",
                        }}
                      >
                        {item.itemName || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: isDarkMode ? "#adb5bd" : "#6c757d",
                          fontSize: "13px",
                        }}
                      >
                        {item.itemType || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: isDarkMode ? "#ffffff" : "#212529",
                        }}
                      >
                        {item.qty || 0}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: isDarkMode ? "#ffffff" : "#212529",
                        }}
                      >
                        £{parseFloat(item.costPrice || 0).toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontWeight: "600",
                          color: isDarkMode ? "#ffffff" : "#212529",
                        }}
                      >
                        £{parseFloat(item.subTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment Links Section */}
        {paymentLinks.length > 0 && (
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>
              <i className="fa fa-credit-card" style={{ color: themeColor }}></i>
              <span>Payment Links ({paymentLinks.length})</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {paymentLinks.map((link, idx) => (
                <div
                  key={link.id || idx}
                  style={{
                    backgroundColor: isDarkMode ? "#495057" : "#ffffff",
                    padding: "16px",
                    borderRadius: "8px",
                    border: `1px solid ${isDarkMode ? "#5a6268" : "#dee2e6"}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                    <div>
                      <div style={{ fontWeight: "600", color: isDarkMode ? "#ffffff" : "#212529", marginBottom: "4px" }}>
                        {link.itemDescription || "Payment Link"}
                      </div>
                      <div style={{ fontSize: "12px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                        Code: {link.paymentLinkCode || "N/A"}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor:
                          link.paymentLinkStatus === "Active"
                            ? "#28a745"
                            : link.isPaid
                            ? "#17a2b8"
                            : "#6c757d",
                        color: "#ffffff",
                      }}
                    >
                      {link.isPaid ? "Paid" : link.paymentLinkStatus || "Pending"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                    <span>
                      <i className="fa fa-pound-sign me-1"></i>
                      {parseFloat(link.amount || 0).toFixed(2)} {link.currency || "GBP"}
                    </span>
                    {link.paymentLinkExpiryDate && (
                      <span>
                        <i className="fa fa-clock me-1"></i>
                        Expires: {new Date(link.paymentLinkExpiryDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div style={sectionCardStyle}>
          <div style={sectionTitleStyle}>
            <i className="fa fa-info-circle" style={{ color: themeColor }}></i>
            <span>Additional Information</span>
          </div>
          <div style={infoRowStyle}>
            <div style={labelStyle}>Occasion</div>
            <div style={valueStyle}>{reservation.occasion || "N/A"}</div>
          </div>
          {reservation.specialRequest && (
            <div style={infoRowStyle}>
              <div style={labelStyle}>Special Request</div>
              <div style={valueStyle}>{reservation.specialRequest}</div>
            </div>
          )}
          <div style={infoRowStyle}>
            <div style={labelStyle}>Advance Deposit Required</div>
            <div style={valueStyle}>
              £{parseFloat(reservation.advanceDepositRequiredAmount || 0).toFixed(2)}
            </div>
          </div>
          <div style={infoRowStyle}>
            <div style={labelStyle}>Advance Deposit Paid</div>
            <div style={valueStyle}>
              {reservation.advanceDepositPaid ? (
                <span style={{ color: "#28a745" }}>
                  <i className="fa fa-check-circle me-1"></i>Yes - £
                  {parseFloat(reservation.advanceDepositAmount || 0).toFixed(2)}
                </span>
              ) : (
                <span style={{ color: "#6c757d" }}>
                  <i className="fa fa-times-circle me-1"></i>No
                </span>
              )}
            </div>
          </div>
          <div style={infoRowStyle}>
            <div style={labelStyle}>Table Merge Required</div>
            <div style={valueStyle}>
              {reservation.isTableMergeRequired ? (
                <span style={{ color: "#ffc107" }}>
                  <i className="fa fa-check-circle me-1"></i>Yes
                </span>
              ) : (
                <span style={{ color: "#6c757d" }}>
                  <i className="fa fa-times-circle me-1"></i>No
                </span>
              )}
            </div>
          </div>
          <div style={infoRowStyle}>
            <div style={labelStyle}>Can Confirm via SMS</div>
            <div style={valueStyle}>
              {reservation.canConfirmViaSMS ? (
                <span style={{ color: "#28a745" }}>
                  <i className="fa fa-check-circle me-1"></i>Yes
                </span>
              ) : (
                <span style={{ color: "#6c757d" }}>
                  <i className="fa fa-times-circle me-1"></i>No
                </span>
              )}
            </div>
          </div>
          {reservation.createdBy && (
            <div style={infoRowStyle}>
              <div style={labelStyle}>Created By</div>
              <div style={valueStyle}>{reservation.createdBy}</div>
            </div>
          )}
          {reservation.createdDateTime && (
            <div style={{ ...infoRowStyle, borderBottom: "none" }}>
              <div style={labelStyle}>Created Date</div>
              <div style={valueStyle}>
                {new Date(reservation.createdDateTime).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const toggleReservationStatus = async (id, currentStatus) => {
    try {
      const updatedReservations = allReservations.map((reservation) =>
        reservation.id === id
          ? {
              ...reservation,
              reservationStatus:
                currentStatus === "Active" ? "Cancelled" : "Active",
            }
          : reservation
      );
      setAllReservations(updatedReservations);

      // TODO: Wire this to a proper status-update API when available.
      // Currently, we only update UI state to avoid calling a non-existing endpoint.
    } catch (error) {
      showErrorToast("Failed to update reservation status");
      setAllReservations(allReservations);
    }
  };

  const renderNoDataMessage = () => {
    if (loading || allReservations.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No reservations found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Reservations</li>
          </ol>
          <h1 className="page-header mb-0">Manage Reservations</h1>
        </div>
      </div>

      {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

      {showGridToolbar && (
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Reservations"
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by customer name, table..."
          bodyFlush
          topFilters={
            <>
              <ModernGridToolbarFilterPill
                iconClass="fa fa-info-circle"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={reservationStatusSelectValue}
                  onChange={(val) =>
                    dispatch(
                      setFilterField({
                        pageKey,
                        field: "reservationStatusFilter",
                        value: val?.value ?? null,
                      })
                    )
                  }
                  options={reservationStatusOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Statuses"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar">
                <SimpleDateRangePicker
                  startDate={filterStartDate ? new Date(`${filterStartDate}T00:00:00`) : null}
                  endDate={filterEndDate ? new Date(`${filterEndDate}T00:00:00`) : null}
                  maxDate={null}
                  onChange={([start, end]) => {
                    const formatFilterDate = (value) => {
                      if (!value) return "";
                      const date = new Date(value);
                      if (Number.isNaN(date.getTime())) return "";
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const day = String(date.getDate()).padStart(2, "0");
                      return `${year}-${month}-${day}`;
                    };
                    dispatch(setFilterField({ pageKey, field: "filterStartDate", value: formatFilterDate(start) }));
                    dispatch(setFilterField({ pageKey, field: "filterEndDate", value: formatFilterDate(end) }));
                  }}
                  placeholder="Date Range"
                  className="filter-dropdown"
                  monthsShown={1}
                />
              </ModernGridToolbarFilterPill>
            </>
          }
          headerControls={
            <ColumnSelection
              visibleColumns={visibleColumns}
              setVisibleColumns={(cols) => dispatch(setVisibleColumns({ pageKey, columns: cols }))}
              defaultColumns={defaultVisibleColumns}
              pageKey={pageKey}
            />
          }
        >
          <div className="px-3 pb-3">
            <GridSectionLoader loading={loading} minHeight="280px">
            {renderNoDataMessage()}
            {allReservations.length > 0 && (
            <>
            <div className="table-responsive" style={{ minHeight: "200px" }}>
              <table className="table table-hover" style={{ "--modern-grid-sort-active": themeColor }}>
                <thead 
                  className="bg-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    {visibleColumns.customerName && (
                      <SortableColumnHeader sortField={RESERVATION_SORT_FIELDS.customerName} sortBy={sortBy} sortOrder={sortOrder} onSort={handleColumnSort}>Customer Name</SortableColumnHeader>
                    )}
                    {visibleColumns.dateTime && (
                      <SortableColumnHeader sortField={RESERVATION_SORT_FIELDS.dateTime} sortBy={sortBy} sortOrder={sortOrder} onSort={handleColumnSort}>Date & Time</SortableColumnHeader>
                    )}
                    {visibleColumns.guests && (
                      <th className="pt-2 pb-2 text-center">Guests</th>
                    )}
                    {visibleColumns.reservationType && (
                      <th className="pt-2 pb-2 text-center">Type</th>
                    )}
                    {visibleColumns.tableNo && (
                      <th className="pt-2 pb-2 text-center">Tables & Service Area</th>
                    )}
                    {visibleColumns.textReminder && (
                      <th className="pt-2 pb-2 text-center">Text Reminder</th>
                    )}
                    {visibleColumns.status && (
                      <th className="pt-2 pb-2 text-center">Status</th>
                    )}
                    <th className="pt-2 pb-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((reservation) => (
                    <tr key={reservation.id} style={{ cursor: "pointer" }}>
                      {visibleColumns.customerName && (
                        <td
                          className="align-middle"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {reservation.customerName && reservation.customerName.trim()
                            ? reservation.customerName
                            : reservation.customerMobileNumber || "N/A"}
                        </td>
                      )}
                      {visibleColumns.dateTime && (
                        <td
                          className="align-middle"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {reservation.startDateTime
                            ? new Date(
                                reservation.startDateTime
                              ).toLocaleString()
                            : "N/A"}
                        </td>
                      )}
                      {visibleColumns.guests && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {reservation.numberofGuest || "N/A"}
                        </td>
                      )}
                      {visibleColumns.reservationType && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {getReservationTypeBadge(reservation)}
                        </td>
                      )}
                      {visibleColumns.tableNo && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {formatTablesWithServiceArea(reservation)}
                        </td>
                      )}
                      {visibleColumns.textReminder && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {reservation.isCustomerWantTextReminder
                            ? "Yes"
                            : "No"}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleRowClick(reservation)}
                        >
                          {reservation.reservationStatus || "N/A"}
                        </td>
                      )}
                      <td 
                        className="align-middle text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                          <TableActionsItem
                            danger
                            icon="fa fa-trash"
                            onClick={() => handleDeleteReservation(reservation)}
                          >
                            Delete
                          </TableActionsItem>
                        </TableActionsMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ModernGridPagerFooter
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalRecords={totalRecords}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
            />
            </>
            )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      )}

      {/* Reservation Detail Modal */}
      <CustomModal
        isOpen={showDetailModal}
        onClose={handleCloseModal}
        title="Reservation Details"
        subtitle={
          selectedReservation
            ? `Reservation #${selectedReservation.id}`
            : ""
        }
        showButton={false}
        modalSize="modal-xl"
        minHeight="500px"
        maxHeight="90vh"
        isCentered={true}
      >
        {renderReservationDetails()}
      </CustomModal>
    </div>
  );
};

export default ManageReservations;